import graphql from "babel-plugin-relay/macro";
import { useAtom } from "jotai";
import { useState } from "react";
import { useFragment } from "react-relay";

import {
  GiftCardsModal,
  SquareGiftCardEntryModalWrapper,
} from "@/features/GiftCards";
import { RainchecksModal } from "@/features/Rainchecks";
import { useTrackEvent } from "@/utils/Analytics/Mixpanel/hooks/useTrackEvent";

import {
  isGiftCardModalOpenAtom,
  isRaincheckModalOpenAtom,
} from "../../atoms/stores/";
import { useAddPaymentGiftCardToBooking } from "../../hooks/useAddPaymentGiftCardToBooking/useAddPaymentGiftCardToBooking";
import { useAddPaymentRaincheckToBooking } from "../../hooks/useAddPaymentRaincheckToBooking/useAddPaymentRaincheckToBooking";
import { BookingCreditSelectModals_booking$key } from "./__generated__/BookingCreditSelectModals_booking.graphql";

export const BookingCreditSelectModals = ({
  bookingRef,
}: {
  bookingRef: BookingCreditSelectModals_booking$key | null;
}) => {
  const booking = useFragment(
    BookingCreditSelectModalsFragmentString,
    bookingRef
  );
  const [shouldGiftCardModalBeOpen, setShouldGiftCardModalBeOpen] = useAtom(
    isGiftCardModalOpenAtom
  );

  const [addPaymentGiftCardToBooking, isAddingPaymentGiftCardToBooking] =
    useAddPaymentGiftCardToBooking();

  const [addPaymentRaincheckToBooking, isAddingPaymentRaincheckToBooking] =
    useAddPaymentRaincheckToBooking();

  const trackEvent = useTrackEvent();

  const bookingId = booking?.id ?? "";

  const handleAddPaymentGiftCardToBooking = (giftCardId: string) => {
    trackEvent("BookingDetail.GiftCardApplied", {
      bookingId,
    });

    addPaymentGiftCardToBooking(
      {
        giftCardId,
        bookingId,
      },
      (bookingId: string) => {
        setShouldGiftCardModalBeOpen(false);
      }
    );
  };

  const [isSquareGiftCardEntryModalOpen, setIsSquareGiftCardEntryModalOpen] =
    useState(false);

  const handleAddNewGiftCardClicked = (isOpen: boolean) => {
    trackEvent("BookingDetail.AddNewGiftCardClicked", {
      bookingId,
    });
    setIsSquareGiftCardEntryModalOpen(isOpen);
  };

  const [shouldRainCheckModalBeOpen, setShouldRainCheckModalBeOpen] = useAtom(
    isRaincheckModalOpenAtom
  );

  const handleAddRaincheckToBooking = (raincheckId: string) => {
    addPaymentRaincheckToBooking(
      {
        raincheckId,
        bookingId,
      },
      (bookingId: string) => {
        setShouldRainCheckModalBeOpen(false);
      }
    );
  };

  const appliedRaincheckIds: string[] =
    booking?.paymentRainchecks?.edges?.map((edge) => edge?.node?.id ?? "") ??
    [];
  const appliedGiftCardIds: string[] =
    booking?.paymentGiftCards?.edges?.map((edge) => edge?.node?.id ?? "") ?? [];
  const facilityId = booking?.startSlot?.agenda?.facility?.id ?? null;

  return (
    <>
      <GiftCardsModal
        isOpen={shouldGiftCardModalBeOpen}
        setIsOpen={setShouldGiftCardModalBeOpen}
        setIsSquareModalOpen={handleAddNewGiftCardClicked}
        onSelectGiftCard={handleAddPaymentGiftCardToBooking}
        isAddingGiftCard={isAddingPaymentGiftCardToBooking}
        appliedGiftCardIds={appliedGiftCardIds}
        activeOnly
        showTotalBalance
      />

      <SquareGiftCardEntryModalWrapper
        isOpen={isSquareGiftCardEntryModalOpen}
        setIsOpen={setIsSquareGiftCardEntryModalOpen}
        onCreateGiftCard={handleAddPaymentGiftCardToBooking}
      />
      {shouldRainCheckModalBeOpen ? (
        <RainchecksModal
          isOpen={shouldRainCheckModalBeOpen}
          setIsOpen={setShouldRainCheckModalBeOpen}
          redeemableAtFacilityId={facilityId}
          onSelectRaincheck={handleAddRaincheckToBooking}
          isLoading={isAddingPaymentRaincheckToBooking}
          appliedRaincheckIds={appliedRaincheckIds}
          activeOnly
          showTotalBalance
        />
      ) : null}
    </>
  );
};

export const BookingCreditSelectModalsFragmentString = graphql`
  fragment BookingCreditSelectModals_booking on Booking {
    id
    startSlot {
      agenda {
        facility {
          id
        }
      }
    }
    ... on StandardBooking {
      paymentGiftCards {
        edges {
          node {
            id
          }
        }
      }
      paymentRainchecks {
        edges {
          node {
            id
          }
        }
      }
    }
    ... on LessonBooking {
      paymentGiftCards {
        edges {
          node {
            id
          }
        }
      }
      paymentRainchecks {
        edges {
          node {
            id
          }
        }
      }
    }
  }
`;
